import React, { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { logAudit } from "../../utils/audit";
import { useToast } from "../../components/ui/ToastProvider";
import { useConfirm } from "../../components/ui/ConfirmProvider";
import { mapFirebaseError } from "../../utils/userErrors";

export default function TaskTemplatesCard({ familyId, familySettings, isPro, canManage = true }) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [name, setName] = useState("");
  const [choreTitle, setChoreTitle] = useState("");
  const [chorePoints, setChorePoints] = useState(0);
  const [choreFrequency, setChoreFrequency] = useState("once");
  const [choreRepeatType, setChoreRepeatType] = useState("daily");
  const [choreCustomDays, setChoreCustomDays] = useState([]);
  const [choreRepeatDay, setChoreRepeatDay] = useState(1);
  const [choreExpiryTime, setChoreExpiryTime] = useState("");
  const [templateChores, setTemplateChores] = useState([]);
  const [errorText, setErrorText] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState("");

  const templates = familySettings?.taskTemplates || [];
  const presets = familySettings?.presetTimes || [];

  const toggleCustomDay = (day) => {
    setChoreCustomDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const addChore = () => {
    if (!canManage) return;
    if (!choreTitle.trim()) {
      setErrorText("Chore title is required.");
      return;
    }
    setErrorText("");
    setTemplateChores(prev => [
      ...prev,
      {
        id: Date.now().toString(),
        title: choreTitle.trim(),
        points: Number(chorePoints) || 0,
        frequency: choreFrequency,
        repeatType: choreRepeatType,
        customDays: choreRepeatType === "custom" ? choreCustomDays : [],
        repeatDay: choreRepeatType === "weekly" ? choreRepeatDay : 1,
        expiryTime: choreExpiryTime || ""
      }
    ]);
    setChoreTitle("");
    setChorePoints(0);
    setChoreFrequency("once");
    setChoreRepeatType("daily");
    setChoreCustomDays([]);
    setChoreRepeatDay(1);
    setChoreExpiryTime("");
  };

  const removeChore = (id) => {
    setTemplateChores(prev => prev.filter(c => c.id !== id));
  };

  const addTemplate = async () => {
    if (!canManage) return;
    if (!name.trim()) {
      setErrorText("Template name is required.");
      return;
    }
    if (templateChores.length === 0) {
      setErrorText("Add at least one chore before saving.");
      return;
    }
    setErrorText("");
    setSavingTemplate(true);
    const newTemplate = {
      id: Date.now().toString(),
      name: name.trim(),
      chores: templateChores
    };
    try {
      const nextTemplates = [...templates, newTemplate];
      await updateDoc(doc(db, "families", familyId), { taskTemplates: nextTemplates });
      await logAudit({ familyId, action: "create", entity: "template", meta: { name: newTemplate.name } });
      setName("");
      setTemplateChores([]);
      showToast({ type: "success", message: "Template saved." });
    } catch (error) {
      setErrorText(mapFirebaseError(error, "Unable to save template."));
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!canManage) return;
    const template = templates.find((entry) => entry.id === id);
    const approved = await confirm({
      title: "Delete template?",
      message: `Delete "${template?.name || "this template"}"?`,
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true
    });
    if (!approved) return;
    setDeletingTemplateId(id);
    try {
      const nextTemplates = templates.filter(t => t.id !== id);
      await updateDoc(doc(db, "families", familyId), { taskTemplates: nextTemplates });
      await logAudit({ familyId, action: "delete", entity: "template", meta: { templateId: id } });
      showToast({ message: "Template removed." });
    } catch (error) {
      setErrorText(mapFirebaseError(error, "Unable to delete template."));
    } finally {
      setDeletingTemplateId("");
    }
  };

  return (
    <div className="glass parent-card">
      <div className="header-flex">
        <h2>Task Templates</h2>
      </div>
      <div className="card-body">
        {!isPro && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>Upgrade to Pro to create task templates.</p>
        )}
        {isPro && !canManage && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
        )}
        {errorText && (
          <small className="pin-error" role="alert">
            {errorText}
          </small>
        )}
        {isPro && (
        <>
        <input
          placeholder="Template name (e.g. School Morning)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canManage || savingTemplate}
        />
        <input
          placeholder="Chore title (e.g. Pack backpack)"
          value={choreTitle}
          onChange={(e) => setChoreTitle(e.target.value)}
          disabled={!canManage || savingTemplate}
        />
        <div style={{ display: "flex", gap: "10px" }}>
          <input
            type="number"
            placeholder="Pts"
            value={chorePoints}
            onChange={(e) => setChorePoints(e.target.value)}
            style={{ width: "80px" }}
            disabled={!canManage || savingTemplate}
          />
          <select value={choreFrequency} onChange={(e) => setChoreFrequency(e.target.value)} style={{ flex: 1 }} disabled={!canManage || savingTemplate}>
            <option value="once">Once</option>
            <option value="repeat">Repeat</option>
          </select>
        </div>

        {choreFrequency === "repeat" && (
          <select value={choreRepeatType} onChange={(e) => setChoreRepeatType(e.target.value)} style={{ marginTop: "10px", width: "100%" }} disabled={!canManage || savingTemplate}>
            <option value="daily">Daily</option>
            <option value="weekdays">Weekdays (Mon-Fri)</option>
            <option value="weekends">Weekends (Sat-Sun)</option>
            <option value="weekly">Weekly (Specific Day)</option>
            <option value="custom">Custom Days</option>
          </select>
        )}

        {choreFrequency === "repeat" && choreRepeatType === "weekly" && (
          <select value={choreRepeatDay} onChange={(e) => setChoreRepeatDay(Number(e.target.value))} style={{ marginTop: "10px", width: "100%" }} disabled={!canManage || savingTemplate}>
            {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((dayName, dayNum) => (
              <option key={dayName} value={dayNum}>{dayName}</option>
            ))}
          </select>
        )}

        {choreFrequency === "repeat" && choreRepeatType === "custom" && (
          <div style={{ marginTop: "10px", padding: "10px", border: "1px solid var(--accent)", borderRadius: "4px" }}>
            <p style={{ margin: "0 0 8px 0", fontSize: "0.9rem" }}>Select days:</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((dayName, dayNum) => (
                <button
                  key={dayNum}
                  onClick={() => toggleCustomDay(dayNum)}
                  style={{
                    padding: "8px",
                    backgroundColor: choreCustomDays.includes(dayNum) ? "var(--accent)" : "var(--surface)",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    fontWeight: choreCustomDays.includes(dayNum) ? "bold" : "normal"
                  }}
                  disabled={!canManage || savingTemplate}
                >
                  {dayName}
                </button>
              ))}
            </div>
          </div>
        )}

        {presets.length > 0 && (
          <div style={{ marginTop: "10px" }}>
            <select onChange={(e) => setChoreExpiryTime(e.target.value)} value={choreExpiryTime} style={{ width: "100%", marginBottom: "5px" }} disabled={!canManage || savingTemplate}>
              <option value="">Select Preset (optional)</option>
              {presets.map(p => <option key={p.id} value={p.time}>{p.name} ({p.time})</option>)}
            </select>
          </div>
        )}
        <input type="time" value={choreExpiryTime} onChange={(e) => setChoreExpiryTime(e.target.value)} style={{ width: "100%" }} disabled={!canManage || savingTemplate} />

        <button className="btn-nav" onClick={addChore} style={{ width: "100%", marginTop: "10px" }} disabled={!canManage || savingTemplate}>
          Add Chore to Template
        </button>

        {templateChores.length > 0 && (
          <div style={{ marginTop: "10px" }}>
            {templateChores.map(chore => (
              <div key={chore.id} className="parent-list-item" style={{ gap: "8px" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <strong>{chore.title}</strong>
                  <small style={{ opacity: 0.7 }}>⭐ {chore.points} {chore.frequency === "repeat" ? `• ${chore.repeatType}` : ""}</small>
                </div>
                <button className="btn-nav" style={{ color: "var(--danger)" }} onClick={() => removeChore(chore.id)} disabled={!canManage || savingTemplate}>
                  Remove
                </button>
              </div>
            ))}
            <button className="btn-nav success-btn" onClick={addTemplate} style={{ width: "100%", marginTop: "10px" }} disabled={!canManage || savingTemplate}>
              {savingTemplate ? "Saving..." : "Save Template"}
            </button>
          </div>
        )}

        {templates.length === 0 && (
          <p style={{ textAlign: "center", opacity: 0.6, marginTop: "10px" }}>No custom templates yet.</p>
        )}
        {templates.map(t => {
          const choreCount = Array.isArray(t.chores) && t.chores.length > 0 ? t.chores.length : 1;
          return (
          <div key={t.id} className="parent-list-item" style={{ gap: "8px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <strong>{t.name || "Template"}</strong>
              <small style={{ opacity: 0.7 }}>{choreCount} chores</small>
            </div>
            <button
              className="btn-nav"
              style={{ color: "var(--danger)" }}
              onClick={() => deleteTemplate(t.id)}
              disabled={!canManage || deletingTemplateId === t.id}
            >
              {deletingTemplateId === t.id ? "Removing..." : "Remove"}
            </button>
          </div>
        );
        })}
        </>
        )}
      </div>
    </div>
  );
}
